import type { AccountType, Industry } from '../../types/account.js';
import { INDUSTRY_LABELS } from '../../types/account.js';
import type {
  PortalAudienceReach,
  PortalAudienceRule,
  PortalLibrarySection,
} from '../../types/portalLibrary.js';
import { PORTAL_LIBRARY_SECTION_LABELS } from '../../types/portalLibrary.js';
import { PORTAL_OPERATIONAL_TYPES } from '../portalScope.js';
import {
  isSectionInStrategyNav,
  resolveStrategyNav,
  strategySectionAudienceLabel,
} from '../portalStrategyNav.js';

// CLIENT PORTAL LIBRARY — AUDIENCE RULES (brief §2 + Amendment 1 §D). PURE: no
// Firestore, no clock, no resolver of its own.
//
// ⚠️⚠️ THIS FILE DECIDES WHICH CLIENT SEES WHICH DOCUMENT. It is a FILTER ON TOP
// OF a resolved PortalScope, never a source of access: an audience rule can
// only remove tiles from what a scope may see, it can never add an account to
// the scope. The only function here that answers "may this CLIENT see this
// tile" is `selectVisibleLibraryEntries` (visibility.ts), and it takes a
// branded PortalScope as a REQUIRED argument.
//
// ⚠️ `matchesAudience` is exported for the reach preview and for tests. The
// reach preview evaluates rules against the whole roster — deliberately, since
// it is a STAFF count ("this will appear for N accounts"), not a visibility
// decision, and it returns numbers rather than documents.

/**
 * What a rule is evaluated against — one account, with everything a rule can
 * ask about it resolved SERVER-SIDE beforehand.
 */
export type AudienceAccount = {
  id: string;
  accountType: AccountType;
  industry: Industry | null;
  /**
   * The marque, from the canonical `computeBrandAncestorMap` (Amendment 1 §D).
   * ⚠️ NEVER re-derived here — a second resolver is the second-authority defect.
   */
  marqueId: string | null;
  /** This account's id FOLLOWED BY every ancestor's, walking parentAccountId up. */
  lineageIds: readonly string[];
};

/** Does ONE rule match ONE account? */
export function ruleMatchesAccount(rule: PortalAudienceRule, account: AudienceAccount): boolean {
  switch (rule.kind) {
    // ⚠️ "every ADMITTED client", not "every account": it still only ever sees
    // the accounts it is handed, which for a client is their own scope.
    case 'everyone':
      return true;
    case 'industry':
      return account.industry !== null && account.industry === rule.industry;
    case 'accountSubtree':
      return account.lineageIds.includes(rule.accountId);
    // ⚠️ NOT a subtree query: a dealership does not sit under its marque.
    case 'marque':
      return account.marqueId !== null && account.marqueId === rule.brandAccountId;
  }
}

/**
 * Does ANY account match ANY rule?
 *
 * ⚠️⚠️ EMPTY IS NOT ABSENT. An empty rule list matches NOBODY — it must never
 * fall through to "show everyone". The check is written out explicitly rather
 * than left to `some` over an empty array, so the rule reads as a rule.
 * ⚠️ And an empty ACCOUNT list (an admitted client with no operational scope)
 * also matches nothing, `everyone` included.
 */
export function matchesAudience(
  rules: readonly PortalAudienceRule[],
  accounts: readonly AudienceAccount[],
): boolean {
  if (rules.length === 0) return false;
  if (accounts.length === 0) return false;
  return accounts.some((a) => rules.some((r) => ruleMatchesAccount(r, a)));
}

/**
 * Build the evaluation records from the roster. The BRAND MAP is passed in —
 * the caller gets it from the accounts module's `computeBrandAncestorMap`.
 */
export function buildAudienceAccounts(
  roster: readonly {
    id: string;
    parentAccountId: string | null;
    accountType: AccountType;
    industry: Industry | null | undefined;
  }[],
  brandByAccountId: ReadonlyMap<string, string | null>,
): Map<string, AudienceAccount> {
  const parentById = new Map(roster.map((a) => [a.id, a.parentAccountId]));
  const out = new Map<string, AudienceAccount>();
  for (const a of roster) {
    const lineage: string[] = [];
    const seen = new Set<string>();
    let cursor: string | null = a.id;
    // Cycle-guarded: the roster is validated against cycles on write, but a
    // corrupted doc must not hang a request.
    while (cursor !== null && !seen.has(cursor)) {
      seen.add(cursor);
      lineage.push(cursor);
      cursor = parentById.get(cursor) ?? null;
    }
    out.set(a.id, {
      id: a.id,
      accountType: a.accountType,
      industry: a.industry ?? null,
      marqueId: brandByAccountId.get(a.id) ?? null,
      lineageIds: lineage,
    });
  }
  return out;
}

/** A plain-English rule label, names resolved from the LIVE roster. */
export function describeAudienceRule(
  rule: PortalAudienceRule,
  nameById: ReadonlyMap<string, string>,
): string {
  switch (rule.kind) {
    case 'everyone':
      return 'Every client with portal access';
    case 'industry':
      return `Industry: ${INDUSTRY_LABELS[rule.industry]}`;
    case 'accountSubtree':
      return `${nameById.get(rule.accountId) ?? 'An account that no longer exists'} and everything under it`;
    case 'marque':
      return `Marque: ${nameById.get(rule.brandAccountId) ?? 'A brand that no longer exists'}`;
  }
}

/**
 * "This will appear for N accounts across M brands" — computed from the rules
 * AS ENTERED, over the operational accounts (the set a PortalScope's data scope
 * is made of). Per-rule counts are what let a zero be explained by the rule
 * that matched nothing.
 */
export function audienceReach(
  rules: readonly PortalAudienceRule[],
  accounts: Iterable<AudienceAccount>,
  nameById: ReadonlyMap<string, string>,
  /**
   * Operational accounts some portal user can see today. ⚠️ REQUIRED, so both
   * numbers always come out of this one function — "N match" and "M have portal
   * access" cannot be computed from two different match sets.
   */
  portalAccessAccountIds: ReadonlySet<string>,
  /**
   * The section being published to (Round 6 §3). ⚠️ REQUIRED, for the same
   * reason as the access set: "N match" and "K can see this section" must come
   * out of one match set, or the refusal below could disagree with the line
   * the publisher reads.
   */
  section: PortalLibrarySection,
): PortalAudienceReach {
  const operational = [...accounts].filter((a) =>
    PORTAL_OPERATIONAL_TYPES.includes(a.accountType),
  );
  const matched = operational.filter((a) => matchesAudience(rules, [a]));
  const brands = new Set(matched.map((a) => a.marqueId).filter((b): b is string => b !== null));
  // ⚠️ A SUBSET OF `matched`. Per ACCOUNT, through the same `resolveStrategyNav`
  // the viewer's nav uses. ⚠️ KNOWN CONSERVATIVE EDGE: a dealership inside a
  // MIXED scope (a group owning a gym AND a car yard) sees the franchise sections
  // through its viewer's scope, which a per-account count cannot know — so this
  // can undercount for that case. Undercounting is the safe direction: it can
  // refuse a publish that would have reached someone, never approve one that
  // reaches nobody.
  const seeing = matched.filter((a) =>
    isSectionInStrategyNav(section, resolveStrategyNav([a.industry])),
  );
  return {
    accountCount: matched.length,
    brandCount: brands.size,
    // ⚠️ A SUBSET OF `seeing` (and so of `matched`), never a separate evaluation
    // of the rules.
    accountsWithPortalAccess: seeing.filter((a) => portalAccessAccountIds.has(a.id)).length,
    section,
    accountsSeeingSection: seeing.length,
    perRule: rules.map((rule) => ({
      rule,
      label: describeAudienceRule(rule, nameById),
      accountCount: operational.filter((a) => ruleMatchesAccount(rule, a)).length,
    })),
  };
}

/**
 * Why a publish must be refused, or null. ⚠️ A tile nobody can see is always a
 * mistake and is silent otherwise (Amendment 2 §D), so zero reach is REFUSED
 * with the rules that matched nothing named.
 */
export function reachRefusal(reach: PortalAudienceReach): string | null {
  if (reach.perRule.length === 0) {
    return 'Choose who this is for — a tile with no audience is shown to nobody.';
  }
  if (reach.accountCount === 0) {
    const dead = reach.perRule.filter((r) => r.accountCount === 0).map((r) => `“${r.label}”`);
    return `This would appear for no accounts: ${dead.join(', ')} matched nothing.`;
  }
  // ⚠️⚠️ ROUND 6 §3 — THE SAME SHAPE, ONE LEVEL DOWN. The rules matched accounts,
  // but none of them has this SECTION in their nav, so the tile would sit where
  // nobody in its audience can reach it. It names the section, because the fix
  // is usually the section rather than the audience.
  if (reach.accountsSeeingSection === 0) {
    const label = PORTAL_LIBRARY_SECTION_LABELS[reach.section];
    const who = strategySectionAudienceLabel(reach.section);
    const n = reach.accountCount;
    return `None of the ${n} matched account${n === 1 ? '' : 's'} can see ${label}${
      who ? ` — it is shown to ${who} only` : ''
    }. Choose a different section or audience.`;
  }
  return null;
}

/** Stable identity for a rule, so a duplicate rule is collapsed on save. */
export function audienceRuleKey(rule: PortalAudienceRule): string {
  switch (rule.kind) {
    case 'everyone':
      return 'everyone';
    case 'industry':
      return `industry:${rule.industry}`;
    case 'accountSubtree':
      return `accountSubtree:${rule.accountId}`;
    case 'marque':
      return `marque:${rule.brandAccountId}`;
  }
}

export function dedupeAudience(rules: readonly PortalAudienceRule[]): PortalAudienceRule[] {
  const seen = new Set<string>();
  const out: PortalAudienceRule[] = [];
  for (const r of rules) {
    const k = audienceRuleKey(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}
